import {userHovercardPath, userIdHovercardPath} from '@github-ui/paths'

import {
  type BaseHovercardOptions,
  type HovercardAttributesResult,
  buildHovercardAttributes,
  useHovercardAttributesBase,
} from './shared'

type UserLoginOptions = BaseHovercardOptions & {login: string | undefined}
type UserIdOptions = BaseHovercardOptions & {userId: number | undefined}

/**
 * Options for user hovercards. Either login or userId must be provided.
 */
export type UserHovercardOptions = UserLoginOptions | UserIdOptions

/**
 * Generate hovercard attributes for a user.
 */
function userHovercardAttributes(options: UserHovercardOptions): HovercardAttributesResult {
  let url: string | null = null

  if ('userId' in options && options.userId != null) {
    url = userIdHovercardPath({userId: options.userId})
  } else if ('login' in options && options.login) {
    url = userHovercardPath({owner: options.login})
  }

  if (!url) return {}

  return buildHovercardAttributes(url, 'user', options.tracking ?? true)
}

/**
 * React hook for user hovercard attributes.
 * Handles SSR and user preferences automatically.
 *
 * @example
 * ```tsx
 * function UserLink({ login }: { login: string }) {
 *   const hovercardAttrs = useHovercardAttributesForUser({login})
 *   return <a href={`/${login}`} {...hovercardAttrs}>{login}</a>
 * }
 *
 * function UserAvatar({ userId }: { userId: number }) {
 *   const hovercardAttrs = useHovercardAttributesForUser({userId})
 *   return <img {...hovercardAttrs} src={`/avatars/u/${userId}`} />
 * }
 * ```
 */
export function useHovercardAttributesForUser(options: UserHovercardOptions): HovercardAttributesResult {
  return useHovercardAttributesBase(userHovercardAttributes(options))
}
